import React, { useEffect, useState, useRef } from "react";
import "./homestyle.css";
import Group from "../../assets/images/Groups.png";
import FadeLoader from "react-spinners/FadeLoader";
import TextareaAutosize from 'react-textarea-autosize';
import { useChatContext } from '../../context/ChatContext';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Import icons for attachment
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import { Download, ExternalLink } from 'lucide-react';

const MESSAGE_BODY_SELECTOR = '.message-inner-div, .message-plan-doc-text';

function MessagePlanDocWrap({ children, attachments, planDocActions }) {
  const wrapRef = useRef(null);
  const barSlotRef = useRef(null);

  useEffect(() => {
    const syncDownloadWidth = () => {
      const body = wrapRef.current?.querySelector(MESSAGE_BODY_SELECTOR);
      const barSlot = barSlotRef.current;
      if (!body || !barSlot) return;
      barSlot.style.width = `${body.offsetWidth}px`;
    };

    const observer = new ResizeObserver(syncDownloadWidth);
    const observeBody = () => {
      observer.disconnect();
      const body = wrapRef.current?.querySelector(MESSAGE_BODY_SELECTOR);
      if (body) observer.observe(body);
      syncDownloadWidth();
    };

    observeBody();
    const rafId = requestAnimationFrame(observeBody);
    window.addEventListener('resize', syncDownloadWidth);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      window.removeEventListener('resize', syncDownloadWidth);
    };
  }, [children, attachments, planDocActions]);

  return (
    <div ref={wrapRef} className="message-plan-doc-wrap">
      {children}
      {attachments}
      <div ref={barSlotRef} className="message-plan-doc-bar-sync">
        {planDocActions}
      </div>
    </div>
  );
}

const OuterInnerPage = (
  {
    fromTFT,
    loaderText,
    allMessage,
    message,
    setMessage,
    handleSend,
    BotTyping,
    visibleMsgBox,
    editingIndex,
    editedMessage,
    setEditedMessage,
    handleEditClick,
    handleSaveClick,
    setEditingIndex,
    initializeChat,
    chatLoading,
    highLightBtn,
    handleBottom,
    AllbuttomMessage,
    showEmptyStateForId,
    emptyStateId,
    planDocReady,
    planDocMeta,
    finalPlanMessageIndex,
    isStreamingResponse,
    onDownloadPlan,
    onSaveToGoogleDocs,
    onOpenPlanDoc,
    googleSaving,
    googleDriveConnected,
    googleDriveEmail,
  }
) => {
  const { initialStart } = useChatContext();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // New state for file attachments
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  useEffect(() => {
    window.onload = () => {
      window.scrollTo(0, document.body.scrollHeight);
    };
  }, []);

  const handleCopy = (text) => {
    const cleanedText = text.replace(/[*#]/g, '').replace(/(?<!\w)-|-(?!\w)/g, "•").replace(/<br\s*\/?>/gi, '\n').replace(/"/g, '');
    
    navigator.clipboard.writeText(cleanedText)
      .then(() => toast.success("Copied to clipboard!"))
      .catch((err) => toast.error("Failed to copy: ", err));
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
     document.body.classList.add("afterchatbot");
    return () => {
      document.body.classList.remove("afterchatbot");
    };
  }, [allMessage]);

  // File handling functions
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      const isValidType = file.type === 'application/pdf' || file.type.startsWith('image/');
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      
      if (!isValidType) {
        toast.error(`${file.name} is not a valid file type. Only PDF and images are allowed.`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64Data = e.target.result;
          
          setAttachedFiles(prev => [...prev, {
            name: file.name,
            type: file.type,
            size: file.size,
            base64: base64Data
          }]);
          
          setPreviewUrls(prev => [...prev, { 
            name: file.name, 
            url: file.type.startsWith('image/') ? base64Data : null, 
            type: file.type.startsWith('image/') ? 'image' : 'pdf' 
          }]);
        };
        reader.readAsDataURL(file);
      });
    }
    
    event.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  // ✅ NEW FUNCTION - Render attachment box for messages with attachedFiles
  const renderAttachmentBox = (msg) => {
    // Check if message has attachedFiles property and it's not empty
    if (!msg.attachedFiles || msg.attachedFiles.length === 0) return null;

    return (
      <div style={{
        marginTop: '12px',
        padding: '12px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{
          fontSize: '11px',
          color: '#666',
          marginBottom: '8px',
          fontWeight: '600'
        }}>
          📎 Attachments ({msg.attachedFiles.length})
        </div>
        <div style={{
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          {msg.attachedFiles.map((file, idx) => (
            <div key={idx} style={{
              border: '1px solid #ddd',
              borderRadius: '6px',
              padding: '8px 12px',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '150px',
              maxWidth: '250px'
            }}>
              {file.type.startsWith('image/') ? (
                <>
                  <ImageIcon style={{ color: '#2c68a1', fontSize: '20px' }} />
                  <img 
                    src={file.base64} 
                    alt={file.name}
                    style={{
                      width: '35px',
                      height: '35px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    onClick={() => window.open(file.base64, '_blank')}
                    title="Click to open"
                  />
                </>
              ) : (
                <PictureAsPdfIcon style={{ color: '#d32f2f', fontSize: '35px' }} />
              )}
              <span style={{ 
                fontSize: '11px',
                color: '#333',
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                flex: 1
              }}>
                {file.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ✅ Modified handleSend wrapper to pass attachedFiles (allow send with only attachments, no text)
  const handleSendWrapper = () => {
    const hasText = message.trim() !== "";
    const hasAttachments = attachedFiles && attachedFiles.length > 0;
    if (!hasText && !hasAttachments) return;

    // Pass attachedFiles to parent's handleSend
    handleSend(attachedFiles);

    // Clear after send
    setAttachedFiles([]);
    setPreviewUrls([]);
  };

  const shouldShowPlanDocActions = (index, msg) =>
    planDocReady &&
    !isStreamingResponse &&
    !BotTyping &&
    !chatLoading &&
    finalPlanMessageIndex === index &&
    msg?.sender === "assistant";

  const SaveToDocsIcon = () => (
    <svg
      className="plan-doc-btn-icon"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M4.5 2.5h5.2L12.5 5.3V13a1 1 0 01-1 1H4.5a1 1 0 01-1-1V3.5a1 1 0 011-1z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M9.5 2.5V5.5H12.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M5.5 8h5M5.5 10h5M5.5 12h3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
      <path d="M8 6.25V8.5M6.875 7.375h2.25" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  );

  const renderPlanDocActions = (index, msg) => {
    if (!shouldShowPlanDocActions(index, msg)) {
      return null;
    }

    const openButton = planDocMeta?.googleDocsLink ? (
      <button
        type="button"
        className="plan-doc-btn plan-doc-btn-secondary"
        onClick={onOpenPlanDoc}
      >
        <ExternalLink size={16} strokeWidth={2} className="plan-doc-btn-icon" />
        Open
      </button>
    ) : (
      <button
        type="button"
        className="plan-doc-btn plan-doc-btn-secondary"
        disabled
      >
        <ExternalLink size={16} strokeWidth={2} className="plan-doc-btn-icon" />
        Open
      </button>
    );

    return (
      <div className="plan-doc-actions-bar plan-doc-actions-inline">
        <div className="plan-doc-status">
          <img
            className="plan-doc-status-copy"
            onClick={() => handleCopy(msg.message)}
            src={Group}
            alt="Copy"
          />
          <span className="plan-doc-status-text">Plan ready</span>
        </div>
        <div className="plan-doc-actions-buttons">
          <button
            type="button"
            className="plan-doc-btn plan-doc-btn-download"
            onClick={onDownloadPlan}
          >
            <Download size={16} strokeWidth={2} className="plan-doc-btn-icon" />
            Download Word
          </button>
          <button
            type="button"
            className="plan-doc-btn plan-doc-btn-secondary"
            onClick={onSaveToGoogleDocs}
            disabled={googleSaving}
          >
            <SaveToDocsIcon />
            {googleSaving ? "Saving..." : "Save to Docs"}
          </button>
          {openButton}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="home-inner-page content" id="content">
        {
          chatLoading && !fromTFT
            ?
            <div className="spinner-box">
              <FadeLoader color="#2c68a1"
                height={50}
                width={12}
                margin={8}
                radius={4}
              />
              {/* {loaderText && <p style={{ marginTop: '40px', color: '#2c68a1', fontWeight: 'bold' }}>
                We realize this is taking longer than usual. Please wait a minute. Do not refresh your screen.
              </p>} */}
            </div>
            :
            <div className={true ? "output-container output-tft" : "output-container "}>

              <div className="output-outer-content">

                <div className="   ">

                  <div className="home-inner-page">
                    {showEmptyStateForId && emptyStateId && (
                      <div className="given-container" style={{ margin: '24px auto', padding: '20px', textAlign: 'center', maxWidth: '480px', borderRadius: '8px', background: '#f5f5f5', color: '#333' }}>
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          No conversation found for this ID (ID: {emptyStateId}). The link may be invalid or the conversation may have been removed.
                        </div>
                      </div>
                    )}
                    {visibleMsgBox && (
                      <div className="chatbox-conent-inner">

                        <div className="chatbox">
                          {allMessage.map((msg, index) => (

                            index === 0 && allMessage.length > 0 ? (

                              true ?
                                <div
                                  key={index}
                                  className={`message ${
                                    true ? msg.sender === "user"
                                      ? "main-conatiner-user"
                                      : "given-container"
                                    : msg.sender === "user"
                                      ? "write-question"
                                      : "given-answer"
                                    }`}
                                >
                                  <div className="message-content-inner">
                                    {shouldShowPlanDocActions(index, msg) ? (
                                      <MessagePlanDocWrap
                                        attachments={renderAttachmentBox(msg)}
                                        planDocActions={renderPlanDocActions(index, msg)}
                                      >
                                        <div className="message-plan-doc-text" style={{ whiteSpace: "pre-wrap" }}>
                                          {allMessage[0].message}
                                        </div>
                                      </MessagePlanDocWrap>
                                    ) : (
                                      <>
                                        <div style={{ whiteSpace: "pre-wrap" }}>
                                          {allMessage[0].message}
                                        </div>
                                        {renderAttachmentBox(msg)}
                                        {allMessage[0].sender !== "user" && (
                                          <div className="message-copy-wrap">
                                            <img
                                              className="group-image message-copy-btn"
                                              onClick={() => handleCopy(allMessage[0].message)}
                                              style={{ cursor: "pointer", display: "block", marginTop: "5px", width: "20px", height: "20px" }}
                                              src={Group}
                                              alt="Copy"
                                            />
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                                :
                                ""
                            )
                              : (

                                <div
                                  key={index}
                                  className={`message ${
                                    true ? msg.sender === "user"
                                      ? "main-conatiner-user"
                                      : "given-container"
                                    : msg.sender === "user"
                                      ? "write-question"
                                      : "given-answer"
                                    }`}
                                >


                                  {editingIndex === index ? (
                                    <>
                                      <textarea
                                        value={editedMessage}
                                        onChange={(e) => setEditedMessage(e.target.value)}
                                      />

                                    </>


                                  ) : (
                                    <div className="message-content-inner">
                                      {shouldShowPlanDocActions(index, msg) ? (
                                        <MessagePlanDocWrap
                                          attachments={renderAttachmentBox(msg)}
                                          planDocActions={renderPlanDocActions(index, msg)}
                                        >
                                          <div
                                            className="message-inner-div"
                                            dangerouslySetInnerHTML={{
                                              __html: (() => {
                                                let raw = msg.message;
                                                raw = raw.replace(/"/g, '');

                                                raw = raw.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
                                                raw = raw.replace(/\*(.*?)\*/g, "<b>$1</b>");
                                                raw = raw.replace(/^\s*$/gm, '');
                                                raw = raw.replace(/(^|\n)((\s*-\s.*(\n|$))+)/g, (match, p1, p2) => {
                                                  const listItems = p2
                                                    .trim()
                                                    .split('\n')
                                                    .map((line) => line.replace(/^\s*-\s(.*)/, '<li>$1</li>'))
                                                    .join('');

                                                  return `${p1}<ul>${listItems}</ul>`;
                                                });

                                                raw = raw.replace(/^#+ (.*?)(\n|$)/gm, "<h5 style='margin-bottom: 15px; margin-left: 0;'>$1</h5>");
                                                raw = raw.replace(/\n/g, "<br>");
                                                raw = raw.replace(/<\/ul><br\s*\/?>/gi, "</ul>");
                                                raw = raw.replace(/(<br\s*\/?>\s*){2,}/gi, "<br>");

                                                return raw.replace(
                                                  /(<h5.*?>.*?<\/h5>)/g,
                                                  (match) => `</div>${match}<div style="margin-left: 20px; margin-bottom: 20px;">`
                                                ).replace(/^/, '<div   style="margin-left: 0px; margin-bottom: 4px;">')
                                                  .concat('</div>')
                                                  .replace(/<div[^>]*>(\s|<br\s*\/?>)*<\/div>/gi, "");
                                              })(),
                                            }}
                                          />
                                        </MessagePlanDocWrap>
                                      ) : (
                                        <>
                                          <div
                                            className={
                                              true ? "message-inner-div" : "message-inner"}
                                            dangerouslySetInnerHTML={{
                                              __html: (() => {
                                                let raw = msg.message;
                                                raw = raw.replace(/"/g, '');

                                                raw = raw.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
                                                raw = raw.replace(/\*(.*?)\*/g, "<b>$1</b>");
                                                raw = raw.replace(/^\s*$/gm, '');
                                                raw = raw.replace(/(^|\n)((\s*-\s.*(\n|$))+)/g, (match, p1, p2) => {
                                                  const listItems = p2
                                                    .trim()
                                                    .split('\n')
                                                    .map((line) => line.replace(/^\s*-\s(.*)/, '<li>$1</li>'))
                                                    .join('');

                                                  return `${p1}<ul>${listItems}</ul>`;
                                                });

                                                raw = raw.replace(/^#+ (.*?)(\n|$)/gm, "<h5 style='margin-bottom: 15px; margin-left: 0;'>$1</h5>");
                                                raw = raw.replace(/\n/g, "<br>");
                                                raw = raw.replace(/<\/ul><br\s*\/?>/gi, "</ul>");
                                                raw = raw.replace(/(<br\s*\/?>\s*){2,}/gi, "<br>");

                                                return raw.replace(
                                                  /(<h5.*?>.*?<\/h5>)/g,
                                                  (match) => `</div>${match}<div style="margin-left: 20px; margin-bottom: 20px;">`
                                                ).replace(/^/, '<div   style="margin-left: 0px; margin-bottom: 4px;">')
                                                  .concat('</div>')
                                                  .replace(/<div[^>]*>(\s|<br\s*\/?>)*<\/div>/gi, "");
                                              })(),
                                            }}
                                          />
                                          {renderAttachmentBox(msg)}
                                          {msg.sender !== "user" && (
                                            <div className="message-copy-wrap">
                                              <img
                                                className="group-image message-copy-btn"
                                                onClick={() => handleCopy(msg.message)}
                                                style={{ cursor: "pointer", display: "block", marginTop: "5px", width: "20px", height: "20px" }}
                                                src={Group}
                                                alt="Copy"
                                              />
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  )}

                                </div>

                              )
                          ))}

                          

                          {/* Bot Typing Indicator */}
                          {BotTyping && (
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px', marginBottom: '20px', color: '#555' }}>
                              <FadeLoader
                                color="#2c68a1"
                                height={14}
                                width={5.5}
                                margin={1}
                                radius={0}
                              />
                              <span style={{ marginLeft: '8px' }}>Typing...</span>
                            </div>
                          )}

                          {/* Scroll Reference */}
                          <div ref={messagesEndRef} />

                        </div>

                      </div>
                    )}

                  </div>

                </div>
              </div>

              {/* Fixed Bottom Section */}
              <div className="inner-images-content">

                {/* File Preview Section */}

                {previewUrls.length > 0 && (
                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    padding: '10px',
                    flexWrap: 'wrap',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    marginBottom: '10px'
                  }}>
                    {previewUrls.map((preview, index) => (
                      <div key={index} style={{
                        position: 'relative',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '8px',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {preview.type === 'image' ? (
                          <>
                            <ImageIcon style={{ color: '#2c68a1' }} />
                            <img 
                              src={preview.url} 
                              alt={preview.name}
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'cover',
                                borderRadius: '4px'
                              }}
                            />
                          </>
                        ) : (
                          <PictureAsPdfIcon style={{ color: '#d32f2f', fontSize: '50px' }} />
                        )}
                        <span style={{ fontSize: '12px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {preview.name}
                        </span>
                        <CloseIcon
                          onClick={() => handleRemoveFile(index)}
                          style={{
                            cursor: 'pointer',
                            fontSize: '18px',
                            color: '#666',
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            padding: '2px'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div className="input-field-btn field-input-inn" style={{ position: 'relative' }}>
                  <div className="outer-textare-inn">
                   <TextareaAutosize
                    minRows={1}
                    maxRows={8}
                    className="input-field"
                    placeholder="Ask a question or describe what you need..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        const hasText = message.trim() !== "";
                        const hasAttachments = attachedFiles && attachedFiles.length > 0;
                        if (!hasText && !hasAttachments) return;
                        e.preventDefault();
                        handleSendWrapper();
                      }
                    }}
                  />
                  
                   {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                  
                  {/* Attachment button */}
                  <button
                    onClick={handleAttachClick}
                    className="translate-inn"
                    style={{
                
                      left: 'auto',
                      top: 'auto',
                      transform: 'translateY(-17%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '2px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                      position: 'absolute',
                      bottom: '17px',
                      right: '100px',
                    }}
                    title="Attach PDF or Image"
                  >
                    <AttachFileIcon style={{ color: '#2c68a1', fontSize: '24px' }} />
                  </button>
                  </div>
                  
                  
                  
                  
                  
                  
                  
                  <div className="file-ref-inner">
                 

                  <button 
                    className="submit-btn" 
                    type="button"
                    onClick={handleSendWrapper}
                  >
                    Submit
                    {/* <img src={UpperArrow} /> */}

                  </button>
 </div>
                  
                </div>

                {
                true ? "" :
                  <div className="buttons-cont-flex" >
                    {initialStart !== "UnitPlanning" && <button onClick={() => AllbuttomMessage("Create a unit plan", "unit")}
                      className={highLightBtn === "UnitPlanning" ? "high-light-btn" : ""}
                    >Unit Plan</button>}

                    {initialStart !== "LessonPlanning" && <button onClick={() => AllbuttomMessage("Create a lesson plan", "lesson")}
                      className={`assignment ${highLightBtn === "LessonPlanning" ? "high-light-btn" : ""}`}
                    >Lesson Plan</button>}

                    {initialStart !== "Assessment" && <button onClick={() => AllbuttomMessage(" Create a assessment", "assessment")}
                      className={`assignment ${highLightBtn === "Assessment" ? "high-light-btn" : ""}`}
                    >Assessment</button>}
                  </div>
                }
              </div>

            </div>
        }
      </div>

    </>
  );
}
export default OuterInnerPage;